import { MAX_LICENSE_LENGTH, MAX_TEXT_LENGTH, validateCertification } from '../../core/profile';
import { isVerifiableState, type CertificationInput } from '../../core/types';
import type { VisitorCertification } from '../../core/payload';
import type { OwnerCertification } from '../api';
import { badgeText, stateName } from '../format';
import { EntrySection, type EntryOps, type Values } from './EntryForm';
import { VerifyButton } from './VerifyButton';

/** "Verified by 240 as <checked name>, <date>": text and an icon, never colour alone (R3, R19). */
export function Badge({ checkedName, checkedAt }: { checkedName: string; checkedAt: string }) {
  return <p className="badge">
    <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l2.4 2.1 3.2-.3.9 3.1 2.8 1.6-1.2 3 1.2 3-2.8 1.6-.9 3.1-3.2-.3L12 22l-2.4-2.1-3.2.3-.9-3.1-2.8-1.6 1.2-3-1.2-3 2.8-1.6.9-3.1 3.2.3z" />
      <path d="M8.5 12.5l2.3 2.3 4.7-5" />
    </svg>
    <span>{badgeText(checkedName, checkedAt)}</span>
  </p>;
}

type Row = { id: string; state: string; area: string; verified: { checkedName: string; checkedAt: string } | null; owner?: OwnerCertification };

const toBody = (displayName: string) => (v: Values): CertificationInput => {
  const state = String(v.state ?? '');
  return {
    state,
    area: String(v.area ?? ''),
    // Only Texas and Florida are checked against a name; any other state carries the profile's (R4).
    nameOnCertificate: isVerifiableState(state) ? String(v.nameOnCertificate ?? '') : displayName,
    licenseNumber: state === 'FL' ? String(v.licenseNumber ?? '').trim() || null : null,
  };
};

/**
 * Certifications (R2–R5). Texas and Florida rows show the name checked against the state and
 * a Verify button with its live status; other states are listed with neither (R4). A visitor
 * sees state, area, and the badge, and nothing about a check that did not end in one.
 */
export function CertificationList(props:
  | { mode: 'visitor'; certifications: readonly VisitorCertification[] }
  | { mode: 'owner'; certifications: readonly OwnerCertification[]; ops: EntryOps<CertificationInput>; displayName: string; verifyEnabled: boolean; onVerify(id: string): Promise<void> }) {
  const rows: Row[] = props.mode === 'owner'
    ? props.certifications.map((c) => ({ id: c.id, state: c.state, area: c.area, owner: c, verified: c.verification?.status === 'verified' ? c.verification : null }))
    : props.certifications.map((c, i) => ({ id: `c${i}`, state: c.state, area: c.area, verified: c.verified ?? null }));
  const displayName = props.mode === 'owner' ? props.displayName : '';

  return <EntrySection
    heading="Certifications" noun="certification" entries={rows} ops={props.mode === 'owner' ? props.ops : undefined}
    label={(row) => `${stateName(row.state)} ${row.area}`}
    render={(row) => <>
      <h3>{row.area}</h3>
      <p className="entry-detail">{stateName(row.state)}</p>
      {row.verified && <Badge checkedName={row.verified.checkedName} checkedAt={row.verified.checkedAt} />}
      {props.mode === 'owner' && row.owner?.verifiable && <>
        <dl className="cert-facts">
          <dt>Name on certificate</dt><dd>{row.owner.nameOnCertificate}</dd>
          {row.state === 'FL' && <><dt>Certificate number</dt><dd>{row.owner.licenseNumber ?? 'Not added'}</dd></>}
        </dl>
        <VerifyButton
          // A new answer is due once the checked details change, so the last one is dropped.
          key={`${row.owner.nameOnCertificate}|${row.area}|${row.owner.licenseNumber ?? ''}`}
          certification={row.owner} enabled={props.verifyEnabled} onVerify={props.onVerify} />
      </>}
    </>}
    form={(row) => ({
      fields: [
        { name: 'state', label: 'State', kind: 'state' },
        { name: 'area', label: 'Certification area', kind: 'text', max: MAX_TEXT_LENGTH },
        { name: 'nameOnCertificate', label: 'Name on certificate', kind: 'text', max: MAX_TEXT_LENGTH, show: (v) => isVerifiableState(String(v.state ?? '')) },
        { name: 'licenseNumber', label: 'Certificate number', kind: 'text', max: MAX_LICENSE_LENGTH, show: (v) => v.state === 'FL' },
      ],
      initial: row?.owner
        ? { state: row.owner.state, area: row.owner.area, nameOnCertificate: row.owner.nameOnCertificate, licenseNumber: row.owner.licenseNumber ?? '' }
        : { state: '', area: '', nameOnCertificate: displayName, licenseNumber: '' },
      validate: (v) => {
        const checked = validateCertification(toBody(displayName)(v));
        return checked.ok ? null : checked;
      },
      toBody: toBody(displayName),
    })}
  />;
}
